<template>
    <div class="rc">

        <!-- BTC Price Banner -->
        <div class="rc-price-banner">
            <div class="rc-price-l">
                <div class="rc-btc-icon">
                    <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="9"/><path d="M9 7h5a2 2 0 010 4H9m0 0h5.5a2 2 0 010 4H9m0-4v-4m0 8V7"/>
                    </svg>
                </div>
                <div>
                    <p class="rc-price-lbl">Live BTC Price</p>
                    <p class="rc-price-val">
                        <span v-if="btcPrice">${{ formatFiat(btcPrice) }}</span>
                        <span v-else class="rc-price-loading">Fetching...</span>
                    </p>
                </div>
            </div>
            <button class="rc-refresh-btn" :disabled="refreshing" @click="fetchBtcPrice">
                <svg class="rc-refresh-icon" :class="refreshing ? 'rc-spin' : ''" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                {{ refreshing ? 'Refreshing...' : 'Refresh' }}
            </button>
        </div>

        <!-- Main grid -->
        <div class="rc-grid">

            <!-- Left: conversion form -->
            <div class="rc-card">
                <div class="rc-card-top" :style="`background:linear-gradient(90deg,transparent,rgba(255,111,0,0.4),rgba(255,179,0,0.2),transparent)`"></div>

                <div class="rc-card-head">
                    <div class="rc-head-icon">
                        <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                        </svg>
                    </div>
                    <div>
                        <h2 class="rc-card-title">Convert Rio BTC</h2>
                        <p class="rc-card-sub">Convert your BTC earnings to USDT or E-wallet</p>
                    </div>
                </div>

                <!-- Rio balance pill -->
                <div class="rc-bal-row">
                    <div class="rc-bal-box rc-bal-box--btc">
                        <p class="rc-bal-lbl">Rio Wallet BTC</p>
                        <p class="rc-bal-val" style="color:#FF6F00">₿{{ formatBtc(rioBalance) }}</p>
                        <p class="rc-bal-usd">≈ ${{ btcPrice ? formatFiat(rioBalanceUsd) : '—' }} USD</p>
                    </div>
                    <svg class="rc-arrow" width="20" height="20" fill="none" stroke="rgba(255,111,0,0.6)" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                    </svg>
                    <div class="rc-bal-box" :style="dest === 'ewallet' ? 'border-color:rgba(16,185,129,0.4);background:rgba(16,185,129,0.06);' : 'border-color:rgba(167,93,237,0.4);background:rgba(167,93,237,0.06);'">
                        <p class="rc-bal-lbl">{{ dest === 'ewallet' ? 'E-wallet Balance' : 'USDT Balance' }}</p>
                        <p class="rc-bal-val" :style="dest === 'ewallet' ? 'color:#10b981' : `color:${themeColors.primary}`">
                            ${{ dest === 'ewallet' ? formatFiat(ewalletBalance) : formatFiat(walletBalance) }}
                        </p>
                        <p class="rc-bal-usd" :style="dest === 'ewallet' ? 'color:#10b981' : `color:${themeColors.primary}`">
                            +${{ convertedUsd }} after conversion
                        </p>
                    </div>
                </div>

                <form class="rc-form" @submit.prevent="submitConversion">

                    <!-- BTC amount input -->
                    <div class="rc-field">
                        <div class="rc-field-head">
                            <label class="rc-field-lbl">BTC Amount to Convert</label>
                            <button type="button" class="rc-max-btn" @click="setMax">MAX</button>
                        </div>
                        <div class="rc-input-wrap">
                            <span class="rc-input-pfx" style="color:#FF6F00;font-weight:900">₿</span>
                            <input
                                v-model="btcAmount"
                                type="text"
                                inputmode="decimal"
                                placeholder="0.000000000000"
                                class="rc-input"
                            />
                        </div>
                        <p v-if="parseFloat(btcAmount) > 0 && btcPrice" class="rc-field-hint" style="color:#FF6F00">
                            ≈ ${{ convertedUsd }} USDT
                        </p>
                    </div>

                    <!-- Destination selector -->
                    <div class="rc-field">
                        <label class="rc-field-lbl">Convert to</label>
                        <div class="rc-dest-toggle">
                            <button
                                type="button"
                                class="rc-dest-btn"
                                :class="dest === 'balance' ? 'rc-dest-btn--on' : ''"
                                :style="dest === 'balance' ? `background:linear-gradient(135deg,#7c3aed,${themeColors.primary});color:#0f172a;box-shadow:0 3px 12px rgba(${themeColors.primaryRgb},0.3);` : ''"
                                @click="dest = 'balance'"
                            >
                                <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                                </svg>
                                USDT Balance
                                <span class="rc-dest-bal">${{ formatFiat(walletBalance) }}</span>
                            </button>
                            <button
                                type="button"
                                class="rc-dest-btn"
                                :class="dest === 'ewallet' ? 'rc-dest-btn--on' : ''"
                                :style="dest === 'ewallet' ? 'background:linear-gradient(135deg,#059669,#10b981);color:#0f172a;box-shadow:0 3px 12px rgba(16,185,129,0.3);' : ''"
                                @click="dest = 'ewallet'"
                            >
                                <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                E-wallet Balance
                                <span class="rc-dest-bal">${{ formatFiat(ewalletBalance) }}</span>
                            </button>
                        </div>
                    </div>

                    <!-- Error -->
                    <p v-if="formError" class="rc-err">{{ formError }}</p>

                    <!-- Submit -->
                    <button
                        type="submit"
                        :disabled="!canSubmit || isProcessing"
                        class="rc-submit"
                        :style="canSubmit ? 'background:linear-gradient(135deg,#ff6f00,#ffb74d);color:#0f172a;box-shadow:0 4px 18px rgba(255,111,0,0.35);' : 'background:rgba(255,255,255,0.05);color:rgba(148,163,184,0.4);'"
                    >
                        <svg v-if="isProcessing" class="rc-spin-ic" width="16" height="16" fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:0.25"/>
                            <path fill="currentColor" style="opacity:0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        <svg v-else width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                        </svg>
                        {{ isProcessing ? 'Converting...' : 'Convert Now' }}
                    </button>
                </form>
            </div>

            <!-- Right: summary + info -->
            <div class="rc-right">

                <!-- Conversion preview -->
                <div v-if="parseFloat(btcAmount) > 0 && btcPrice" class="rc-card rc-preview">
                    <div class="rc-card-top" style="background:linear-gradient(90deg,transparent,rgba(255,111,0,0.3),transparent)"></div>
                    <h3 class="rc-section-title" style="color:#FF6F00">Conversion Preview</h3>
                    <div class="rc-rows">
                        <div class="rc-row"><span class="rc-row-lbl">BTC Amount</span><span class="rc-row-val" style="color:#FF6F00">₿{{ formatBtc(btcAmount) }}</span></div>
                        <div class="rc-row"><span class="rc-row-lbl">BTC Price</span><span class="rc-row-val">${{ formatFiat(btcPrice) }}</span></div>
                        <div class="rc-row rc-row--total">
                            <span class="rc-row-lbl--b">You Receive</span>
                            <span class="rc-row-total" :style="dest === 'ewallet' ? 'color:#10b981' : `color:${themeColors.primary}`">${{ convertedUsd }} USDT</span>
                        </div>
                        <div class="rc-row"><span class="rc-row-lbl">To</span><span class="rc-row-val">{{ dest === 'ewallet' ? 'E-wallet' : 'USDT Balance' }}</span></div>
                    </div>
                </div>

                <!-- Info card -->
                <div class="rc-card">
                    <div class="rc-card-top" style="background:linear-gradient(90deg,transparent,rgba(255,111,0,0.2),transparent)"></div>
                    <h3 class="rc-section-title">How It Works</h3>
                    <div class="rc-steps">
                        <div class="rc-step" v-for="(s, i) in steps" :key="i">
                            <div class="rc-step-n" style="background:rgba(255,111,0,0.12);color:#FF6F00;border-color:rgba(255,111,0,0.25)">{{ i+1 }}</div>
                            <p class="rc-step-t">{{ s }}</p>
                        </div>
                    </div>
                    <div class="rc-warn">
                        <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20" style="flex-shrink:0;color:#fbbf24;margin-top:1px">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                        </svg>
                        <p>Conversion uses the live BTC price at time of submission. Rate is locked from the database snapshot.</p>
                    </div>
                </div>

            </div>
        </div>

        <!-- ── CONVERSION HISTORY ── -->
        <div class="rc-hist">
            <div class="rc-hist-head">
                <div class="rc-hist-title-row">
                    <div class="rc-hist-icon">
                        <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                    </div>
                    <h2 class="rc-hist-title">Conversion History</h2>
                </div>
                <span class="rc-hist-count">{{ rioConversions.length }} record{{ rioConversions.length !== 1 ? 's' : '' }}</span>
            </div>

            <!-- Empty state -->
            <div v-if="!rioConversions.length" class="rc-hist-empty">
                <svg width="36" height="36" fill="none" stroke="rgba(255,111,0,0.3)" stroke-width="1.5" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="9"/><path d="M9 7h5a2 2 0 010 4H9m0 0h5.5a2 2 0 010 4H9m0-4v-4m0 8V7"/>
                </svg>
                <p class="rc-hist-empty-txt">No conversions yet. Convert your Rio BTC to get started.</p>
            </div>

            <!-- Table -->
            <div v-else class="rc-hist-table-wrap">
                <table class="rc-hist-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Reference</th>
                            <th>BTC Amount</th>
                            <th>BTC Price</th>
                            <th>USDT Received</th>
                            <th>Destination</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="conv in rioConversions" :key="conv.ref">
                            <td class="rc-hist-date">{{ conv.date }}</td>
                            <td class="rc-hist-ref">{{ conv.ref }}</td>
                            <td class="rc-hist-btc">₿{{ formatBtc(conv.btc_amount) }}</td>
                            <td class="rc-hist-price">${{ formatFiat(conv.btc_price) }}</td>
                            <td class="rc-hist-usdt">+${{ formatFiat(conv.usdt_amount) }}</td>
                            <td>
                                <span class="rc-hist-dest" :class="conv.destination === 'E-wallet' ? 'rc-hist-dest--ew' : 'rc-hist-dest--usdt'">
                                    {{ conv.destination }}
                                </span>
                            </td>
                            <td>
                                <span class="rc-hist-status" :class="`rc-hist-status--${conv.status}`">
                                    {{ conv.status }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { useToast } from '@/composables/useToast.js';

const props = defineProps({
    rioBalance:     { type: [Number, String], default: 0 },
    walletBalance:  { type: [Number, String], default: 0 },
    ewalletBalance: { type: [Number, String], default: 0 },
    rioConversions: { type: Array, default: () => [] },
});

const { showToast } = useToast();
const page = usePage();

const btcPrice    = ref(null);
const refreshing  = ref(false);
const btcAmount   = ref('');
const dest        = ref('ewallet');
const isProcessing = ref(false);
const formError   = ref('');

const steps = [
    'Enter the BTC amount from your Rio Wallet you want to convert.',
    'Choose the destination: USDT Balance or E-wallet Balance.',
    'The live BTC price from the database is used to calculate USDT value.',
    'Your Rio BTC balance decreases and the destination balance increases instantly.',
];

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const hexToRgb = (hex) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return r ? { r: parseInt(r[1],16), g: parseInt(r[2],16), b: parseInt(r[3],16) } : { r:134, g:239, b:172 };
};
const themeColors = computed(() => {
    const rgb = hexToRgb(primaryColor.value);
    return { primary: primaryColor.value, primaryRgb: `${rgb.r},${rgb.g},${rgb.b}` };
});

const rioBalanceUsd = computed(() => {
    if (!btcPrice.value) return 0;
    return parseFloat(props.rioBalance) * btcPrice.value;
});

const convertedUsd = computed(() => {
    const amt = parseFloat(btcAmount.value);
    if (!amt || !btcPrice.value) return '0.00';
    return (amt * btcPrice.value).toFixed(2);
});

const canSubmit = computed(() => {
    const raw = btcAmount.value.trim();
    if (!raw || !/^\d+(\.\d{1,12})?$/.test(raw)) return false;
    const amt = parseFloat(raw);
    if (isNaN(amt) || amt <= 0) return false;
    if (amt > parseFloat(String(props.rioBalance))) return false;
    if (!btcPrice.value) return false;
    return !isProcessing.value;
});

const formatBtc  = (v) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 12 }).format(parseFloat(v) || 0);
const formatFiat = (v) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(v) || 0);

const setMax = () => { btcAmount.value = String(props.rioBalance || '0'); };

const fetchBtcPrice = async () => {
    refreshing.value = true;
    try {
        const res = await fetch('/user/wallet/btc-price');
        const data = await res.json();
        if (data.success) {
            btcPrice.value = data.price_usd;
        } else {
            showToast('BTC price unavailable', 'warning');
        }
    } catch {
        showToast('Failed to fetch BTC price', 'error');
    } finally {
        refreshing.value = false;
    }
};

const submitConversion = () => {
    formError.value = '';
    const raw = btcAmount.value.trim();
    if (!raw || !/^\d+(\.\d{1,12})?$/.test(raw)) { formError.value = 'Enter a valid BTC amount (up to 12 decimal places).'; return; }
    const amt = parseFloat(raw);
    if (isNaN(amt) || amt <= 0) { formError.value = 'Enter a valid BTC amount.'; return; }
    if (amt > parseFloat(String(props.rioBalance))) { formError.value = 'Insufficient Rio BTC balance.'; return; }
    if (!btcPrice.value) { formError.value = 'BTC price not loaded. Please refresh.'; return; }

    isProcessing.value = true;
    router.post('/user/wallet/rio/convert', {
        btc_amount:  raw,
        destination: dest.value,
    }, {
        preserveScroll: true,
        onSuccess: () => {
            showToast('BTC converted successfully!', 'success');
            btcAmount.value = '';
        },
        onError: (errors) => {
            const msg = errors?.error || errors?.btc_amount || errors?.destination || 'Conversion failed.';
            formError.value = msg;
            showToast(msg, 'error');
        },
        onFinish: () => { isProcessing.value = false; },
    });
};

onMounted(fetchBtcPrice);
</script>

<style scoped>
.rc { display: flex; flex-direction: column; gap: 18px; }

/* Price banner */
.rc-price-banner {
    display: flex; align-items: center; justify-content: space-between; gap: 14px;
    padding: 14px 20px; border-radius: 14px;
    background: rgba(255,111,0,0.06); border: 1px solid rgba(255,111,0,0.22);
    flex-wrap: wrap;
}
.rc-price-l { display: flex; align-items: center; gap: 14px; }
.rc-btc-icon {
    width: 42px; height: 42px; border-radius: 12px;
    background: linear-gradient(135deg,#ff6f00,#ffb74d);
    display: flex; align-items: center; justify-content: center;
    color: #0f172a; box-shadow: 0 4px 14px rgba(255,111,0,0.4); flex-shrink: 0;
}
.rc-price-lbl { font-size: 10px; font-weight: 700; color: rgba(255,111,0,0.7); text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 3px; }
.rc-price-val { font-size: 22px; font-weight: 900; color: #FF6F00; margin: 0; line-height: 1; }
.rc-price-loading { font-size: 14px; color: rgba(255,111,0,0.5); }
.rc-refresh-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 8px 16px; border-radius: 10px; border: 1px solid rgba(255,111,0,0.3);
    background: rgba(255,111,0,0.08); color: #FF6F00;
    font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s;
}
.rc-refresh-btn:hover:not(:disabled) { background: rgba(255,111,0,0.15); }
.rc-refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.rc-refresh-icon { flex-shrink: 0; }

/* Grid */
.rc-grid { display: grid; gap: 18px; }
@media(min-width: 900px) { .rc-grid { grid-template-columns: 1fr 320px; align-items: start; } }
.rc-right { display: flex; flex-direction: column; gap: 16px; }

/* Card */
.rc-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 20px; padding: 22px;
    position: relative; overflow: hidden;
}
.rc-card-top { position: absolute; top: 0; left: 10%; right: 10%; height: 1px; pointer-events: none; }
.rc-card-head { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.rc-head-icon {
    width: 44px; height: 44px; border-radius: 13px;
    background: linear-gradient(135deg,#ff6f00,#ffb74d);
    display: flex; align-items: center; justify-content: center;
    color: #0f172a; box-shadow: 0 4px 14px rgba(255,111,0,0.35); flex-shrink: 0;
}
.rc-card-title { font-size: 15px; font-weight: 800; color: #fff; margin: 0 0 3px; }
.rc-card-sub   { font-size: 11.5px; color: rgba(148,163,184,0.5); margin: 0; }

/* Balance row */
.rc-bal-row { display: flex; align-items: center; gap: 12px; margin-bottom: 22px; flex-wrap: wrap; }
.rc-bal-box {
    flex: 1; min-width: 130px; padding: 14px 16px; border-radius: 14px;
    background: rgba(255,111,0,0.05); border: 1px solid rgba(255,111,0,0.2);
    transition: all 0.2s;
}
.rc-bal-box--btc { background: rgba(255,111,0,0.07); border-color: rgba(255,111,0,0.3); }
.rc-bal-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(148,163,184,0.5); margin: 0 0 5px; }
.rc-bal-val { font-size: 18px; font-weight: 900; margin: 0 0 3px; line-height: 1; }
.rc-bal-usd { font-size: 11px; font-weight: 600; color: rgba(148,163,184,0.45); margin: 0; }
.rc-arrow   { flex-shrink: 0; }

/* Form */
.rc-form   { display: flex; flex-direction: column; gap: 18px; }
.rc-field  { display: flex; flex-direction: column; gap: 8px; }
.rc-field-head { display: flex; align-items: center; justify-content: space-between; }
.rc-field-lbl  { font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.85); }
.rc-max-btn {
    font-size: 10px; font-weight: 900; padding: 3px 10px; border-radius: 6px;
    border: 1px solid rgba(255,111,0,0.35); background: rgba(255,111,0,0.1);
    color: #FF6F00; cursor: pointer; letter-spacing: 0.06em; transition: all 0.18s;
}
.rc-max-btn:hover { background: rgba(255,111,0,0.2); }
.rc-input-wrap { position: relative; display: flex; align-items: center; }
.rc-input-pfx  { position: absolute; left: 13px; font-size: 14px; pointer-events: none; }
.rc-input {
    width: 100%; padding: 12px 14px 12px 32px;
    border-radius: 11px; border: 1px solid rgba(255,111,0,0.25);
    background: rgba(255,111,0,0.04); color: #fff;
    font-size: 14px; font-weight: 600; outline: none; transition: border-color 0.18s;
    box-sizing: border-box;
}
.rc-input:focus { border-color: rgba(255,111,0,0.55); background: rgba(255,111,0,0.06); }
.rc-input::placeholder { color: rgba(148,163,184,0.3); }
.rc-field-hint { font-size: 11px; font-weight: 700; margin: 0; }

/* Destination toggle */
.rc-dest-toggle { display: flex; gap: 10px; flex-wrap: wrap; }
.rc-dest-btn {
    flex: 1; min-width: 140px; display: inline-flex; align-items: center; gap: 7px;
    padding: 11px 14px; border-radius: 11px; border: 1px solid rgba(255,255,255,0.1);
    background: rgba(255,255,255,0.04); color: rgba(148,163,184,0.65);
    font-size: 12.5px; font-weight: 700; cursor: pointer; transition: all 0.2s;
}
.rc-dest-btn:hover:not(.rc-dest-btn--on) { border-color: rgba(255,255,255,0.2); color: #fff; }
.rc-dest-btn--on { border-color: transparent; font-weight: 800; }
.rc-dest-bal {
    margin-left: auto; padding: 2px 8px; border-radius: 6px;
    background: rgba(0,0,0,0.2); font-size: 11px; font-weight: 800;
}

.rc-err    { font-size: 11.5px; font-weight: 700; color: #f87171; padding: 9px 12px; border-radius: 9px; background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.22); margin: 0; }
.rc-submit {
    width: 100%; padding: 14px 20px; border-radius: 13px; border: none;
    font-size: 14px; font-weight: 800; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    transition: all 0.2s; letter-spacing: 0.01em;
}
.rc-submit:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.08); }
.rc-submit:disabled { opacity: 0.45; cursor: not-allowed; }

/* Preview & Info */
.rc-preview { }
.rc-section-title { font-size: 13px; font-weight: 800; color: #fff; margin: 0 0 16px; }
.rc-rows { display: flex; flex-direction: column; gap: 10px; }
.rc-row  { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.rc-row-lbl   { font-size: 12px; color: rgba(148,163,184,0.55); font-weight: 600; }
.rc-row-val   { font-size: 13px; font-weight: 700; color: rgba(226,232,240,0.9); }
.rc-row--total { padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.06); }
.rc-row-lbl--b { font-size: 13px; font-weight: 800; color: #fff; }
.rc-row-total  { font-size: 20px; font-weight: 900; }
.rc-steps { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; }
.rc-step  { display: flex; align-items: flex-start; gap: 10px; }
.rc-step-n {
    width: 26px; height: 26px; border-radius: 8px; border: 1px solid;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 900; flex-shrink: 0;
}
.rc-step-t { font-size: 12px; font-weight: 600; color: rgba(148,163,184,0.6); line-height: 1.55; padding-top: 4px; }
.rc-warn {
    display: flex; align-items: flex-start; gap: 9px; padding: 11px 13px; border-radius: 10px;
    background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.2);
    font-size: 11px; font-weight: 600; color: rgba(253,230,138,0.7); line-height: 1.5;
}

/* Animations */
.rc-spin { animation: rcSpin 1s linear infinite; }
.rc-spin-ic { animation: rcSpin 0.85s linear infinite; flex-shrink: 0; }
@keyframes rcSpin { to { transform: rotate(360deg); } }

/* ── History ── */
.rc-hist {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(255,111,0,0.16);
    border-radius: 20px; padding: 22px;
    position: relative; overflow: hidden;
}
.rc-hist::before {
    content:''; position:absolute; top:0; left:10%; right:10%; height:1px;
    background: linear-gradient(90deg,transparent,rgba(255,111,0,0.35),transparent);
    pointer-events: none;
}
.rc-hist-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; margin-bottom: 20px; flex-wrap: wrap;
}
.rc-hist-title-row { display: flex; align-items: center; gap: 10px; }
.rc-hist-icon {
    width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
    background: linear-gradient(135deg,#ff6f00,#ffb74d);
    display: flex; align-items: center; justify-content: center;
    color: #0f172a; box-shadow: 0 3px 12px rgba(255,111,0,0.35);
}
.rc-hist-title { font-size: 14px; font-weight: 800; color: #fff; margin: 0; }
.rc-hist-count {
    font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px;
    background: rgba(255,111,0,0.1); border: 1px solid rgba(255,111,0,0.25);
    color: rgba(255,111,0,0.8);
}

/* Empty */
.rc-hist-empty {
    display: flex; flex-direction: column; align-items: center; gap: 14px;
    padding: 40px 20px; text-align: center;
}
.rc-hist-empty-txt { font-size: 13px; color: rgba(148,163,184,0.45); font-weight: 600; }

/* Table */
.rc-hist-table-wrap { overflow-x: auto; border-radius: 12px; border: 1px solid rgba(255,255,255,0.06); }
.rc-hist-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.rc-hist-table thead tr {
    background: rgba(255,111,0,0.06);
    border-bottom: 1px solid rgba(255,111,0,0.18);
}
.rc-hist-table th {
    padding: 11px 14px; text-align: left; font-size: 11px; font-weight: 700;
    color: rgba(255,111,0,0.7); text-transform: uppercase; letter-spacing: 0.07em;
    white-space: nowrap;
}
.rc-hist-table tbody tr {
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 0.15s;
}
.rc-hist-table tbody tr:last-child { border-bottom: none; }
.rc-hist-table tbody tr:hover { background: rgba(255,111,0,0.04); }
.rc-hist-table td { padding: 12px 14px; vertical-align: middle; white-space: nowrap; }

.rc-hist-date  { font-size: 11.5px; color: rgba(148,163,184,0.55); }
.rc-hist-ref   { font-size: 11px; font-family: monospace; color: rgba(148,163,184,0.5); }
.rc-hist-btc   { font-weight: 700; color: #FF6F00; }
.rc-hist-price { color: rgba(226,232,240,0.7); }
.rc-hist-usdt  { font-weight: 700; color: #10b981; }

.rc-hist-dest {
    display: inline-block; padding: 3px 9px; border-radius: 6px;
    font-size: 11px; font-weight: 700;
}
.rc-hist-dest--ew   { background: rgba(16,185,129,0.12); color: #10b981; border: 1px solid rgba(16,185,129,0.25); }
.rc-hist-dest--usdt { background: rgba(167,93,237,0.12); color: #a75ded; border: 1px solid rgba(167,93,237,0.25); }

.rc-hist-status {
    display: inline-block; padding: 3px 9px; border-radius: 6px;
    font-size: 11px; font-weight: 700; text-transform: capitalize;
}
.rc-hist-status--completed { background: rgba(16,185,129,0.12); color: #10b981; border: 1px solid rgba(16,185,129,0.2); }
.rc-hist-status--pending   { background: rgba(251,191,36,0.12); color: #fbbf24; border: 1px solid rgba(251,191,36,0.2); }
.rc-hist-status--failed    { background: rgba(239,68,68,0.12);  color: #f87171; border: 1px solid rgba(239,68,68,0.2); }
</style>
